import { DailyStock } from '@/api/daily-stock-api';
import { TablePagination } from '@/components/core/async-data-table/table-pagination';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Form } from '@/components/ui/form';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useDebounce } from '@/hooks/use-debounce';
import { useSearchProducts } from '@/services/product-service';
import {
  FilterRequest,
  Product,
  ProductVariant,
  TProductWithRelation,
  VariantStockSearchItem,
} from '@/types/product-type';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { BulkReceiveProductCard } from './bulk-receive-product-card';
import { ProductFilterBar } from './bulk-receive-product-filter-bar';

// Form schema for bulk stock entry
const bulkStockEntrySchema = z.record(
  z.string(), // variant ID as key
  z.number().min(0, 'Quantity must be at least 0').optional(),
);

type BulkStockEntryFormValues = z.infer<typeof bulkStockEntrySchema>;

interface BulkReceiveModalProps {
  isOpen: boolean;
  productId: string | null;
  variantCode?: string | null;
  onSubmit: (data: Record<string, number | undefined>) => Promise<void> | void;
  onCancel: () => void;
}

// Transform VariantStockSearchItem[] to TProductWithRelation[]
function transformVariantsToProductsWithRelations(
  variants: VariantStockSearchItem[],
  variantCode?: string | null,
): TProductWithRelation[] {
  const productMap = new Map<string, TProductWithRelation>();

  variants.forEach((item) => {
    const productIdStr = item.productId.toString();

    if (!productMap.has(productIdStr)) {
      // Create Product object from first variant's product info
      const product: Product = {
        id: productIdStr,
        code: item.saleCode || '',
        saleCode: item.saleCode,
        name: item.productName,
        description: item.description,
        unit: item.unit,
        quantity: null,
        price: item.productPrice,
        cost: null,
        imageUrl: item.productImageUrl,
        sold: item.soldQty,
        pinned: null,
        createdBy: null,
        updatedBy: null,
        deletedBy: null,
        createdAt: null,
        updatedAt: null,
        deletedAt: null,
        isActive: null,
      };

      productMap.set(productIdStr, {
        product,
        productAttributes: [],
        productVariants: [],
      });
    }

    const productData = productMap.get(productIdStr)!;

    // Create DailyStock object from stock fields
    const stock: DailyStock = {
      id: "0", // Not available in VariantStockSearchItem
      storeId: "0", // Not available, may need to get from context
      productId: productIdStr,
      variantId: item.id.toString(),
      stockDate: new Date().toISOString().split('T')[0], // Use today's date
      openQty: item.openQty,
      stockInHand: item.stockInHand,
      soldQty: item.soldQty,
      receiveQty: item.receiveQty,
      adjustInQty: item.adjustInQty,
      adjustOutQty: item.adjustOutQty,
      closingQty: item.closingQty,
      locked: null,
    };

    // Create ProductVariant object
    const variant: ProductVariant = {
      id: item.id.toString(),
      productId: productIdStr,
      storeId: 0, // Not available, may need to get from context
      sku: item.sku,
      name: item.variantName,
      variantKey: item.variantKey || '',
      price: item.price,
      cost: null,
      priceAdjustment: null,
      costAdjustment: null,
      quantity: null,
      imageUrl: item.imageUrl,
      stock,
      sold: item.soldQty,
      productName: item.productName,
      pinned: item.pinned,
      createdBy: null,
      updatedBy: null,
      deletedBy: null,
      createdAt: null,
      updatedAt: null,
      deletedAt: null,
      isActive: null,
    };
    if (variantCode && variantCode === item.sku) {
      productData.productVariants.push(variant);
    }
    else {
      productData.productVariants.push(variant);
    }


  });

  return Array.from(productMap.values());
}

export function BulkReceiveModal({ isOpen, productId, variantCode, onSubmit, onCancel }: BulkReceiveModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const previousProductIdRef = useRef<string | null>(null);
  const previousIsOpenRef = useRef<boolean>(false);
  const isResettingRef = useRef<boolean>(false);

  // Debounce the local search query before updating the actual search query
  const debouncedSearchQuery = useDebounce(localSearchQuery, 500);

  // Update searchQuery when debounced value changes (but skip if we're in the middle of a reset)
  useEffect(() => {
    if (!isResettingRef.current) {
      setSearchQuery(debouncedSearchQuery);
    }
  }, [debouncedSearchQuery]);

  // Build FilterRequest for /products/search endpoint
  const filterRequest: FilterRequest | null = useMemo(() => {
    if (!isOpen) return null;

    const searchProps: FilterRequest['searchProp'] = [];

    // Add product_id filter if provided
    if (productId) {
      searchProps.push({
        propName: 'v.product_id',
        value: productId,
      });
    }

    if (variantCode) {
      searchProps.push({
        propName: 'v.sku',
        value: variantCode,
      });
    }

    // Normalize search query for case-insensitive search
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return {
      filterText: normalizedQuery || null,
      searchProp: searchProps.length > 0 ? searchProps : null,
      page,
      size: pageSize,
      sortBy: 'p.name',
      ordinal: 'asc',
    };
  }, [isOpen, productId, page, pageSize, searchQuery]);

  // Fetch variants using /products/search endpoint
  const {
    items: variants,
    isLoading: isLoadingProducts,
    totalElements,
    refresh,
  } = useSearchProducts(filterRequest);

  // Transform VariantStockSearchItem[] to TProductWithRelation[]
  const productsWithRelations = useMemo(() => {
    if (!isOpen || !variants) return [];
    return transformVariantsToProductsWithRelations(variants, variantCode);
  }, [isOpen, variants, variantCode]);

  // Create initial form values with all variants
  const initialValues = useMemo<BulkStockEntryFormValues>(() => {
    const values: BulkStockEntryFormValues = {};
    productsWithRelations.forEach((productData) => {
      productData.productVariants.forEach((variant) => {
        values[variant.id.toString()] = 0;
      });
    });
    return values;
  }, [productsWithRelations]);

  const form = useForm<BulkStockEntryFormValues>({
    resolver: zodResolver(bulkStockEntrySchema),
    defaultValues: initialValues,
  });

  // Watch all form values for reactive calculations
  const formValues = form.watch();

  // Centralized form reset function
  const resetForm = useCallback(() => {
    form.reset(initialValues);
  }, [form, initialValues]);

  // Reset search and filters when modal opens or productId changes
  useEffect(() => {
    const isFirstOpen = !previousIsOpenRef.current && isOpen;
    const productIdChanged =
      previousProductIdRef.current !== productId && previousProductIdRef.current !== null;

    if (isOpen) {
      // Only reset search/filters on first open or when productId changes
      if (isFirstOpen || productIdChanged) {
        isResettingRef.current = true;
        setSelectedProductId(null);
        // Reset both local and actual search query
        setLocalSearchQuery('');
        setSearchQuery('');
        setPage(0);
        // Clear the reset flag after a brief moment to allow debounce to work normally
        setTimeout(() => {
          isResettingRef.current = false;
        }, 100);
      }
    }

    previousIsOpenRef.current = isOpen;
    previousProductIdRef.current = productId;
  }, [isOpen, productId]);

  // Reset form when products load after modal opens or productId changes
  // Use a separate ref to track if we've already reset for this productId
  const resetFormRef = useRef<string | null>(null);
  useEffect(() => {
    if (isOpen && productsWithRelations.length > 0) {
      const shouldReset = resetFormRef.current !== productId;
      if (shouldReset) {
        resetForm();
        resetFormRef.current = productId;
      }
    }
    if (!isOpen) {
      resetFormRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, productId, productsWithRelations.length]);

  // Reset page when search query changes (but don't reset the search query itself)
  useEffect(() => {
    if (isOpen && searchQuery !== undefined) {
      setPage(0);
    }
  }, [isOpen, searchQuery]);

  const handleSubmit = async (data: BulkStockEntryFormValues) => {
    setIsSubmitting(true);
    try {
      await onSubmit(data);
      refresh();
      // Only reset form on successful submission
      resetForm();
    } catch (error) {
      console.error('Error in bulk receive:', error);
      // Don't reset form on error - let user see what they entered
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    form.handleSubmit(handleSubmit)(e);
  };

  const handleCancel = () => {
    resetForm();
    onCancel();
  };

  // Filter products based on selected product (client-side filtering for UI)
  // Note: Search is now handled by backend via filterText
  const filteredProducts = useMemo(() => {
    let filtered = productsWithRelations;

    // Filter by selected product (client-side UI filter)
    if (selectedProductId) {
      filtered = filtered.filter((p) => p.product.id.toString() === selectedProductId);
    }

    return filtered;
  }, [productsWithRelations, selectedProductId]);

  // Memoize total variants count for filtered products
  const totalVariants = useMemo(
    () => filteredProducts.reduce((sum, p) => sum + p.productVariants.length, 0),
    [filteredProducts],
  );

  // Memoize variants with quantity > 0
  const variantsWithQuantity = useMemo(() => {
    return Object.entries(formValues).filter(([_, quantity]) => quantity && quantity > 0);
  }, [formValues]);

  // Helper function to calculate future stock after adding quantity
  const getFutureStock = useCallback(
    (variant: TProductWithRelation['productVariants'][0]) => {
      const currentStock = variant.stock?.closingQty || 0;
      const addedQuantity = formValues[variant.id.toString()] || 0;
      return currentStock + addedQuantity;
    },
    [formValues],
  );

  // Handle page change
  const handlePageChange = useCallback((_event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  // Handle page size change
  const handleRowsPerPageChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          handleCancel();
        }
      }}
    >
      <DialogContent className="max-w-6xl w-[calc(100vw-1rem)] sm:w-full h-full max-h-[90dvh] flex flex-col p-0 gap-0">
        <DialogHeader className="px-3 pt-3 pb-2 sm:px-4 sm:pt-4 sm:pb-3 md:px-6 md:pt-6 md:pb-4 shrink-0">
          <DialogTitle className="text-base sm:text-lg md:text-xl">Bulk Stock Receive</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={handleFormSubmit}
            className="flex flex-col flex-1 min-h-0 overflow-x-hidden"
          >
            <div className="px-3 pb-2 sm:px-4 sm:pb-3 md:px-6 md:pb-4 shrink-0">
              <ProductFilterBar
                productsWithRelations={productsWithRelations}
                selectedProductId={selectedProductId}
                onProductChange={setSelectedProductId}
                searchQuery={localSearchQuery}
                onSearchChange={setLocalSearchQuery}
              />
            </div>

            <ScrollArea className="flex-1 min-h-0">
              <div className="space-y-2 sm:space-y-3 px-3 sm:px-4 md:px-6 pb-2">
                {isLoadingProducts ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="h-6 w-6 animate-spin mr-2" />
                    <span className="text-sm text-muted-foreground">Loading products...</span>
                  </div>
                ) : filteredProducts.length > 0 ? (
                  filteredProducts.map((productData) => (
                    <BulkReceiveProductCard
                      key={productData.product.id}
                      productData={productData}
                      control={form.control}
                      isSubmitting={isSubmitting}
                      getFutureStock={getFutureStock}
                    />
                  ))
                ) : (
                  <div className="text-sm text-muted-foreground py-12 text-center">
                    No products found
                  </div>
                )}
              </div>
            </ScrollArea>

            {/* Pagination */}
            {!isLoadingProducts && totalElements > 0 && (
              <div className="border-t shrink-0">
                <TablePagination
                  rowsPerPageOptions={[25, 50, 100]}
                  count={totalElements}
                  rowsPerPage={pageSize}
                  page={page}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                />
              </div>
            )}

            <div className="flex flex-col gap-2 sm:gap-3 sm:flex-row sm:justify-between sm:items-center px-3 pt-2 pb-3 sm:px-4 sm:pt-3 sm:pb-4 md:px-6 md:pt-4 md:pb-6 border-t shrink-0">
              <div className="text-xs text-muted-foreground text-center sm:text-left">
                {variantCode && <span>{variantCode}</span>} Total variants: {totalVariants} • Variants with stock: {variantsWithQuantity.length}
              </div>
              <div className="flex gap-2 w-full sm:w-auto">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                  className="flex-1 sm:flex-initial h-10 sm:h-9"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={isSubmitting}
                  size="sm"
                  className="flex-1 sm:flex-initial h-10 sm:h-9"
                >
                  {isSubmitting ? 'Processing...' : 'Receive Stock'}
                </Button>
              </div>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
